/**
 * Unified Panel Manager (GARDEN-UX-004)
 * 
 * Centralized panel/overlay management with consistent:
 * - ESC key handling
 * - Click-outside-to-close
 * - Close buttons
 * - Canvas pointer-events management
 */

export interface PanelConfig {
  id: string;
  element: HTMLElement;
  onClose: () => void;
  closeOnEsc?: boolean;
  closeOnClickOutside?: boolean;
  overlayElement?: HTMLElement | null;
}

class PanelManager {
  private panels: Map<string, PanelConfig> = new Map();
  private isInitialized = false;
  private escHandler: ((e: KeyboardEvent) => void) | null = null;

  initialize(): void {
    if (this.isInitialized) return;
    
    this.escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.closeTopPanel();
      }
    };
    
    document.addEventListener('keydown', this.escHandler);
    this.isInitialized = true;
  }

  destroy(): void {
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }
    this.panels.clear();
    this.isInitialized = false;
  }

  register(config: PanelConfig): void {
    if (!this.isInitialized) {
      this.initialize();
    }
    
    this.panels.set(config.id, config);

    // Set up click-outside handler if overlay is provided
    if (config.closeOnClickOutside !== false && config.overlayElement) {
      config.overlayElement.addEventListener('click', (e) => {
        if (e.target === config.overlayElement) {
          config.onClose();
        }
      });
    }

    // GARDEN-UX-006: Update canvas pointer-events when panel opens
    this.updateCanvasPointerEvents();
  }

  unregister(id: string): void {
    this.panels.delete(id);
    // GARDEN-UX-006: Update canvas pointer-events when panel closes
    this.updateCanvasPointerEvents();
  }

  closeTopPanel(): void {
    // Find the top-most open panel (last registered or highest z-index)
    let topPanel: PanelConfig | null = null;
    let highestZ = -1;

    for (const panel of this.panels.values()) {
      if (!panel.element.isConnected) continue;
      if (panel.closeOnEsc === false) continue;
      
      const zIndex = parseInt(window.getComputedStyle(panel.element).zIndex) || 0;
      if (zIndex > highestZ) {
        highestZ = zIndex;
        topPanel = panel;
      }
    }

    if (topPanel) {
      topPanel.onClose();
    }
  }

  // GARDEN-UX-006: Manage canvas pointer-events based on panel state
  updateCanvasPointerEvents(): void {
    const hasOpenModal = this.hasOpenModal();
    // Use body class to control canvas pointer-events (handled by CSS)
    if (hasOpenModal) {
      document.body.classList.add('has-modal');
    } else {
      document.body.classList.remove('has-modal');
    }
  }

  private hasOpenModal(): boolean {
    for (const panel of this.panels.values()) {
      if (panel.element.isConnected && panel.element.style.display !== 'none') {
        return true;
      }
    }
    return false;
  }
}

// Export singleton
export const panelManager = new PanelManager();

/**
 * Standard close button creator (GARDEN-UX-004)
 */
export function createCloseButton(onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'panel-close-btn';
  btn.setAttribute('aria-label', 'Close');
  btn.innerHTML = '×';
  btn.style.cssText = `
    position: absolute;
    top: 12px;
    right: 12px;
    width: 44px;
    height: 44px;
    min-height: 44px;
    padding: 0;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: rgba(230, 237, 243, 0.85);
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: var(--z-above, 10);
    transition: background 0.15s, transform 0.1s;
  `;
  
  btn.onmouseenter = () => {
    btn.style.background = 'rgba(255, 255, 255, 0.15)';
  };
  btn.onmouseleave = () => {
    btn.style.background = 'rgba(255, 255, 255, 0.08)';
  };
  btn.onmousedown = () => {
    btn.style.transform = 'scale(0.95)';
  };
  btn.onmouseup = () => {
    btn.style.transform = 'scale(1)';
  };
  btn.onclick = onClick;
  
  return btn;
}

/**
 * Standard overlay creator (GARDEN-UX-004)
 */
export function createOverlay(onClick: () => void): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'panel-overlay';
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    z-index: var(--z-modal, 1000);
    display: flex;
    align-items: center;
    justify-content: center;
  `;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      onClick();
    }
  });
  return overlay;
}
